import { normalizeSize } from "../utils";

export const UNITS_ENUM = {
  Ft: "ft",
  M: "m",
  Ft2: "ft²",
  m2: "m²",
  no: "Nos",
};
export const UNITS = Object.values(UNITS_ENUM);

export function resetDerivedFields(handleFieldUpdate, rowIndex) {
  handleFieldUpdate(rowIndex, "UnitWeight", "");
  handleFieldUpdate(rowIndex, "UnitSA", "");
  handleFieldUpdate(rowIndex, "MaterialRate", "");
  handleFieldUpdate(rowIndex, "FabRate", "");
}

export function resetSizeAndUpFields(handleFieldUpdate, rowIndex) {
  handleFieldUpdate(rowIndex, "Material", "");
  handleFieldUpdate(rowIndex, "Material Description", "");
  handleFieldUpdate(rowIndex, "OriginalSizeRange", "");
  handleFieldUpdate(rowIndex, "UnitWeight", "");
  handleFieldUpdate(rowIndex, "UnitSA", "");
  handleFieldUpdate(rowIndex, "MaterialRate", "");
  handleFieldUpdate(rowIndex, "FabRate", "");
}

export function updateDerivedFields(
  allTables,
  filteredSelectedSpecs,
  handleFieldUpdate,
  rowIndex,
  rowData,
) {
  const { Components, Size, Spec, OriginalSizeRange, MaterialDescription } =
    rowData;

  // ... rest of function unchanged
  let i = rowIndex; // for logging context
  const structure_FamiliesTableName = Spec ? `structural_spec_${Spec}` : null;

  // console.log(`[Row ${i}] Looking up estimation table for:`, {
  //   Components,
  //   Spec,
  //   structure_FamiliesTableName,
  // });

  if (!structure_FamiliesTableName) {
    // console.warn(
    //   `[Row ${i}] Cannot generate piping families table name - missing Spec`,
    // );
    resetDerivedFields(handleFieldUpdate, i);
    return;
  }

  const structureFamiliesTable = allTables?.[structure_FamiliesTableName];

  if (!structureFamiliesTable || !structureFamiliesTable.length) {
    // console.warn(
    //   `[Row ${i}] Piping families table not found: ${structure_FamiliesTableName}`,
    // );
    // console.log(
    //   "Available tables:",
    //   Object.keys(allTables).filter((k) => k.includes("piping_families")),
    // );
    resetDerivedFields(handleFieldUpdate, i);
    return;
  }

  const familyRow = structureFamiliesTable.find(
    (cur) => cur.Components === Components,
  );

  if (!familyRow) {
    // console.warn(
    //   `[Row ${i}] Component "${Components}" not found in ${structure_FamiliesTableName}`,
    // );
    // console.log(
    //   "Available components:",
    //   structureFamiliesTable.map((r) => r.Component),
    // );
    resetDerivedFields(handleFieldUpdate, i);
    return;
  }

  const weightTableName = familyRow.Est_Table_Name;

  if (!weightTableName) {
    // console.warn(
    //   `[Row ${i}] Est_Table_Name is empty for Component "${Components}"`,
    // );
    // console.log("Family row data:", familyRow);
    resetDerivedFields(handleFieldUpdate, i);
    return;
  }

  const targetTable = allTables?.[weightTableName] || [];

  if (!weightTableName) {
    // console.warn(
    //   `Cannot generate table name - missing Components or Spec at row ${i}`,
    // );
    return;
  }

  if (!targetTable) {
    // console.warn(`Table not found: ${weightTableName}`);
    return;
  }

  if (!targetTable.length) {
    // console.warn(`Table found but is empty: ${weightTableName}`);
    return;
  }

  const targetRow = targetTable.find(
    (row) => normalizeSize(row.Size) === normalizeSize(Size),
  );

  if (targetRow) {
    const unitWeight = targetRow.Weight;
    if (unitWeight !== undefined) {
      handleFieldUpdate(rowIndex, "UnitWeight", unitWeight);
    }

    const unitSA = targetRow["Surface Area"];
    if (unitSA !== undefined) {
      handleFieldUpdate(rowIndex, "UnitSA", unitSA);
    }

    const materialRate = targetRow["Unit_Cost"] || targetRow["Unit Cost"];
    if (materialRate !== undefined) {
      handleFieldUpdate(rowIndex, "MaterialRate", materialRate);
    }

    const fabRate = targetRow["MH"];
    if (fabRate !== undefined) {
      handleFieldUpdate(rowIndex, "FabRate", fabRate);
    }
  }
}

export function generateSummary(estimationRows, prevSummary) {
  if (!estimationRows) return [];

  // Build a lookup of existing summary rows to preserve user-entered data
  const prevSummaryMap = new Map();
  (prevSummary || []).forEach((r) => {
    if (!r.isSummary && r["Material Description"]) {
      prevSummaryMap.set(r["Material Description"], r);
    }
  });

  const summaryMap = new Map();

  let totalQty = 0;
  let totalWeight = 0;
  let totalMaterialCost = 0;
  let totalWeldHours = 0;
  let totalPainting = 0;
  let totalGalv = 0;
  let totalFireProf = 0;

  const existingRateRow = prevSummary?.find((r) => r.Weight === "Rate");

  estimationRows.forEach((row) => {
    if (row.isSummary) return;

    const {
      Components,
      Qty,
      length,
      Unit,
      UnitWeight,
      UnitSA,
      MaterialRate,
      FabRate,
      Painted,
      Galvanized,
      FireProofed,
    } = row;
    const MaterialDescription = row["Material Description"];

    const parsedLen = parseDimension(length);
    const qty = parseFloat(Qty) || 0;
    const unitWeight = parseFloat(UnitWeight) || 0;
    const unitSA = parseFloat(UnitSA) || 0;
    const materialRate = parseFloat(MaterialRate) || 0;
    const fabRate = parseFloat(FabRate) || 0;

    const Weight =
      qty && parsedLen && unitWeight
        ? +(qty * parsedLen * unitWeight).toFixed(2)
        : 0;

    const totalFabHrs = Weight && fabRate ? +(Weight * fabRate).toFixed(2) : 0;

    const key = MaterialDescription;
    const prev = prevSummaryMap.get(key);

    const current = summaryMap.get(key) || {
      "Material Description": MaterialDescription,
      length,
      procuredQty: prev?.procuredQty ?? "",
      Unit,
      unitCost: prev?.unitCost ?? "Not Added!",
      totalFabHrs: 0,
      Components,
      Weight: 0,
      Qty: 0,
      MaterialCost: 0,
      WeldHours: 0,
      PaintingCost: 0,
      GalvanizingCost: 0,
      FireProofingCost: 0,
    };

    current.Qty += qty;
    current.Weight += Weight;
    current.totalFabHrs += totalFabHrs;

    const matCost = materialRate * Weight;
    current.MaterialCost += matCost;

    const weld = fabRate * Weight;
    current.WeldHours += weld;

    if (Painted) {
      const AREA_COMPONENTS = ["Grating", "Plate"];
      const isAreaBased = AREA_COMPONENTS.includes(Components);
      const paintCost = isAreaBased
        ? qty * parsedLen * unitSA
        : qty * parsedLen * unitSA;
      current.PaintingCost += paintCost;
      totalPainting += paintCost;
    }

    if (Galvanized) {
      current.GalvanizingCost += Weight;
      totalGalv += Weight;
    }

    if (FireProofed) {
      const AREA_COMPONENTS = ["Grating", "Plate"];
      const isAreaBased = AREA_COMPONENTS.includes(Components);
      const fireCost = isAreaBased
        ? qty * parsedLen * unitSA
        : qty * parsedLen * unitSA;
      current.FireProofingCost += fireCost;
      totalFireProf += fireCost;
    }

    summaryMap.set(key, current);

    totalQty += qty;
    totalWeight += Weight;
    totalMaterialCost += matCost;
    totalWeldHours += weld;
  });

  const result = Array.from(summaryMap.values()).map((r) => ({
    ...r,
    Qty: +r.Qty.toFixed(2),
    totalFabHrs: +r.totalFabHrs.toFixed(2),
    Weight: +r.Weight.toFixed(2),
    MaterialCost: +r.MaterialCost.toFixed(2),
    WeldHours: +r.WeldHours.toFixed(2),
    PaintingCost: +r.PaintingCost.toFixed(2),
    GalvanizingCost: +r.GalvanizingCost.toFixed(2),
    FireProofingCost: +r.FireProofingCost.toFixed(2),
    procuredQty: r.procuredQty,
    unitCost: r.unitCost,
  }));

  const totals = {
    totalQty: +totalQty.toFixed(2),
    totalWeight: +totalWeight.toFixed(2),
    totalMaterialCost: +totalMaterialCost.toFixed(2),
    totalWeldHours: +totalWeldHours.toFixed(2),
    totalPainting: +totalPainting.toFixed(2),
    totalGalv: +totalGalv.toFixed(2),
    totalFireProf: +totalFireProf.toFixed(2),
  };

  // Skip the trailing Total/Rate/Total Cost/Cost Summary rows — the column
  // Footers already render per-column totals so those rows would render empty
  // against the current Summary columns.
  void totals;
  void existingRateRow;
  return result;
}

export function generateSummaryRowsWithRates(totals, existingRateRow) {
  const rateRow = {
    "Material Description": "",
    Qty: "",
    Unit: "",
    Weight: "Rate",
    MaterialCost: "",
    WeldHours: existingRateRow?.WeldHours || 0,
    PaintingCost: existingRateRow?.PaintingCost || 0,
    GalvanizingCost: existingRateRow?.GalvanizingCost || 0,
    FireProofingCost: existingRateRow?.FireProofingCost || 0,
    isSummary: true,
  };

  const totalRow = {
    "Material Description": "",
    Qty: "",
    Unit: "",
    Weight: "Total",
    MaterialCost: totals.totalMaterialCost,
    WeldHours: totals.totalWeldHours,
    PaintingCost: totals.totalPainting,
    GalvanizingCost: totals.totalGalv,
    FireProofingCost: totals.totalFireProf,
    isSummary: true,
  };

  const totalCostRow = {
    "Material Description": "",
    Qty: "",
    Unit: "",
    Weight: "Total Cost",
    MaterialCost: +totals.totalMaterialCost.toFixed(2),
    WeldHours: +(totals.totalWeldHours * rateRow.WeldHours).toFixed(2),
    PaintingCost: +(totals.totalPainting * rateRow.PaintingCost).toFixed(2),
    GalvanizingCost: +(totals.totalGalv * rateRow.GalvanizingCost).toFixed(2),
    FireProofingCost: +(
      totals.totalFireProf * rateRow.FireProofingCost
    ).toFixed(2),
    isSummary: true,
  };

  const summaryRow = {
    "Material Description": "",
    Qty: "",
    Unit: "",
    Weight: "Cost Summary",
    MaterialCost: +(
      totalCostRow.MaterialCost +
      totalCostRow.WeldHours +
      totalCostRow.PaintingCost +
      totalCostRow.GalvanizingCost +
      totalCostRow.FireProofingCost
    ).toFixed(2),
    WeldHours: "",
    PaintingCost: "",
    GalvanizingCost: "",
    FireProofingCost: "",
    isSummary: true,
  };

  return [totalRow, rateRow, totalCostRow, summaryRow];
}

export function generateBOM(summary) {
  if (!summary) return [];
  return summary
    .filter((row) => !row.isSummary)
    .map((row, index) => ({
      ItemNo: index + 1,
      "Material Description": row["Material Description"],
      Qty: row.Qty,
      length: row.length,
      Unit: row.Unit,
      Weight: row.Weight,
      MaterialCost: row.MaterialCost,
    }));
}

// Parses dimension values like "2 x 6", "2x6", "2*6" or plain "6"
export function parseDimension(value) {
  if (!value && value !== 0) return 0;
  const str = String(value).replace(/\s/g, "");

  // Handle expressions like 2x6, 2*6, 2×6
  const multiplyMatch = str.match(/^([\d.]+)[xX*×]([\d.]+)$/);
  if (multiplyMatch) {
    return parseFloat(multiplyMatch[1]) * parseFloat(multiplyMatch[2]);
  }

  // Plain number
  const plain = parseFloat(str);
  return isNaN(plain) ? 0 : plain;
}
